import { Component, Input, OnInit } from '@angular/core';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { IntegrationService, CloudIntegrationStatus } from '../../../services/integration.service';
import { BucketService, CloudItem } from '../../../services/bucket.service';

@Component({
  selector: 'app-cloud-sync-dialog',
  templateUrl: './cloud-sync-dialog.component.html',
  styleUrl: './cloud-sync-dialog.component.scss',
})
export class CloudSyncDialogComponent implements OnInit {
  @Input() bucketId = '';

  // Steps: 'provider' | 'browse'
  step: 'provider' | 'browse' = 'provider';

  connectedProviders: CloudIntegrationStatus[] = [];
  loadingProviders = true;

  selectedProvider = '';
  browseItems: CloudItem[] = [];
  loadingItems = false;
  breadcrumb: { id: string; name: string }[] = [];

  selectedItems: Set<string> = new Set();
  syncFrequency = 'never';
  importing = false;

  get providerLabel() {
    const labels: Record<string, string> = {
      google_drive: 'Google Drive',
      dropbox: 'Dropbox',
      sharepoint: 'SharePoint',
    };
    return labels[this.selectedProvider] || this.selectedProvider;
  }

  get currentFolderId() {
    if (this.breadcrumb.length === 0) return 'root';
    return this.breadcrumb[this.breadcrumb.length - 1].id;
  }

  constructor(
    protected dialogRef: NbDialogRef<CloudSyncDialogComponent>,
    private integrationService: IntegrationService,
    private bucketService: BucketService,
    private toastr: NbToastrService,
    private t: TranslateService,
  ) {}

  ngOnInit() {
    this.integrationService.getAllCloudIntegrations().subscribe({
      next: (integrations) => {
        this.connectedProviders = integrations.filter(i => i.connected);
        this.loadingProviders = false;
      },
      error: () => {
        this.loadingProviders = false;
      },
    });
  }

  selectProvider(provider: string) {
    this.selectedProvider = provider;
    this.breadcrumb = [];
    this.step = 'browse';
    this.loadFolder('root');
  }

  loadFolder(folderId: string) {
    this.loadingItems = true;
    this.selectedItems.clear();
    this.bucketService.browseCloud(this.selectedProvider, folderId).subscribe({
      next: (items) => {
        this.browseItems = items;
        this.loadingItems = false;
      },
      error: (err) => {
        this.loadingItems = false;
        if (err?.status === 401) {
          // Token expired — trigger re-auth via OAuth redirect
          this.toastr.warning(
            this.t.instant('components.cloudSync.connectionExpired'),
            this.t.instant('components.cloudSync.connectionExpiredTitle'),
          );
          this.reconnectProvider(this.selectedProvider);
        } else {
          this.toastr.danger(
            this.t.instant('components.cloudSync.browseError'),
            this.t.instant('toasts.error'),
          );
        }
      },
    });
  }

  navigateInto(item: CloudItem) {
    if (item.type !== 'folder') return;
    this.breadcrumb.push({ id: item.id, name: item.name });
    this.loadFolder(item.id);
  }

  navigateTo(index: number) {
    if (index < 0) {
      this.breadcrumb = [];
      this.loadFolder('root');
    } else {
      this.breadcrumb = this.breadcrumb.slice(0, index + 1);
      this.loadFolder(this.breadcrumb[index].id);
    }
  }

  toggleItem(item: CloudItem) {
    if (this.selectedItems.has(item.id)) {
      this.selectedItems.delete(item.id);
    } else {
      this.selectedItems.add(item.id);
    }
  }

  isSelected(item: CloudItem) {
    return this.selectedItems.has(item.id);
  }

  get selectedCount() {
    return this.selectedItems.size;
  }

  onImport() {
    const items = this.browseItems.filter(i => this.selectedItems.has(i.id));
    if (items.length === 0) return;

    this.importing = true;
    this.bucketService.importCloud({
      provider: this.selectedProvider,
      items,
      bucket_id: this.bucketId || undefined,
      sync_frequency: this.syncFrequency,
    }).subscribe({
      next: (result) => {
        this.importing = false;
        this.toastr.success(
          this.t.instant('components.cloudSync.importSuccess', {
            imported: result.imported,
            skipped: result.skipped,
          }),
          this.t.instant('components.cloudSync.importDone'),
        );
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.importing = false;
        if (err?.status === 401) {
          this.toastr.warning(
            this.t.instant('components.cloudSync.connectionExpired'),
            this.t.instant('components.cloudSync.connectionExpiredTitle'),
          );
          this.reconnectProvider(this.selectedProvider);
        } else {
          this.toastr.danger(
            this.t.instant('components.cloudSync.importError'),
            this.t.instant('toasts.error'),
          );
        }
      },
    });
  }

  backToProviders() {
    this.step = 'provider';
    this.selectedProvider = '';
    this.browseItems = [];
    this.breadcrumb = [];
    this.selectedItems.clear();
  }

  reconnectProvider(provider: string) {
    this.integrationService.getCloudAuthUrl(provider).subscribe({
      next: (res) => {
        // Redirect to OAuth — after callback, user lands on /integrations?connected=...
        window.location.href = res.auth_url;
      },
      error: () => {
        this.toastr.danger(
          this.t.instant('components.cloudSync.reconnectError'),
          this.t.instant('toasts.error'),
        );
      },
    });
  }

  close() {
    this.dialogRef.close(false);
  }
}
